// src/components/GallerySection.jsx
import React, { useEffect, useState, useRef } from "react";
import LightboxModal from "./LightBoxModal";
import { getLatestWorks } from "../services/galleryApi";

const BROKEN =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='500'>
      <rect width='100%' height='100%' fill='#e9ecef'/>
    </svg>`
  );

// REACT_APP_API_URL example: https://api.yourdomain.com/api
const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const FILE_BASE = API_BASE.replace(/\/api$/i, "");

const normalize = (src) => {
  if (!src) return null;
  if (/^https?:\/\//i.test(src)) return src;

  const clean = String(src).replace(/^\/+/, "");

  if (/^uploads\//i.test(clean)) {
    return `${FILE_BASE}/${clean}`;
  }
  return `${FILE_BASE}/uploads/${clean}`;
};

function ClampText({ text, lines = 4, className = "" }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const compute = () => {
      // when not expanded, check if clamped content is overflowing
      if (expanded) {
        setIsTruncated(true); // keep button visible as "See less"
        return;
      }
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    };

    compute();

    // re-check on resize (responsive cards)
    const ro = new ResizeObserver(compute);
    ro.observe(el);

    return () => ro.disconnect();
  }, [text, expanded]);

  return (
    <div className={`clamp ${className}`}>
      <div
        ref={ref}
        className={`clamp__text ${expanded ? "is-expanded" : ""}`}
        style={!expanded ? { WebkitLineClamp: lines } : undefined}
      >
        {text}
      </div>

      {isTruncated && (
        <button
          type="button"
          className="clamp__btn"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
}

const GallerySection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [modalItem, setModalItem] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const data = await getLatestWorks();
        if (!mounted) return;

        const normalized = (data || [])
          .slice(0, 8)
          .map((it) => {
            const imgs = Array.isArray(it.images)
              ? it.images.map(normalize).filter(Boolean).slice(0, 4)
              : [];

            while (imgs.length < 4) imgs.push(null);

            return {
              id: it.id,
              title: it.title || "Untitled",
              description: it.description || "",
              cover_image: normalize(it.cover_image) || imgs[0] || null,
              images: imgs,
            };
          })
          .filter((w) => w.cover_image || (w.images || []).some(Boolean));

        setItems(normalized);
      } catch (e) {
        console.error("Gallery fetch failed:", e);
        setErr("Unable to load gallery.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const openModal = (item) => {
    setModalItem(item);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalItem(null);
    document.body.style.overflow = "";
  };

  const openLightbox = (imgs, idx = 0) => {
    const arr = (imgs || []).filter(Boolean).slice(0, 4);
    if (arr.length === 0) return;
    setLightboxImages(arr);
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  return (
    <section id="gallery">
      <h2>Gallery</h2>

      {loading && <p>Loading gallery…</p>}
      {!loading && err && <p className="error-text">{err}</p>}

      {!loading && !err && (
        <div className="gallery-grid">
          {items.map((g) => (
            <article key={g.id} className="card gallery-card">
              <div className="gallery-img-container">
                <img
                  className="gallery-cover-img"
                  src={g.cover_image || BROKEN}
                  alt={g.title}
                  onError={(e) => {
                    e.currentTarget.src = BROKEN;
                    e.currentTarget.style.objectFit = "cover";
                  }}
                />
                <button
                  type="button"
                  className="gallery-details-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(g);
                  }}
                >
                  See Details
                </button>
              </div>

              <div className="card-content">
                <div className="card-title">{g.title}</div>
                <div className="card-desc">
                  <ClampText text={g.description} lines={4} />
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {modalItem && (
        <div
          className="home-modal active"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
          style={{ display: "flex" }}
        >
          <div
            className="home-modal-content"
            role="document"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              className="home-modal-close"
              aria-label="Close"
              onClick={closeModal}
            >
              &times;
            </button>

            <h2>{modalItem.title}</h2>

            <div className="home-modal-gallery-grid">
              {modalItem.images.filter(Boolean).map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`${modalItem.title} ${i + 1}`}
                  onClick={() => openLightbox(modalItem.images, i)}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = BROKEN;
                    e.currentTarget.style.objectFit = "contain";
                  }}
                />
              ))}
            </div>

            {modalItem.description ? (
              <div className="card-desc" style={{ marginTop: "1em" }}>
                <ClampText text={modalItem.description} lines={6} />
              </div>
            ) : null}
          </div>
        </div>
      )}

      <LightboxModal
        isOpen={lightboxOpen}
        images={lightboxImages}
        initialIndex={lightboxIndex}
        onClose={closeLightbox}
      />
    </section>
  );
};

export default GallerySection;
